import React from "react";
import { Form } from "react-crud-form";
import { useNavigate } from "react-router-dom";
import { FormField, FormItem, FormMessage } from "ui/components/form";
import { Input } from "ui/components/input";
import MultiFileUpload from "ui/components/multi-file-upload";
import { FormLabel } from "components/form";
import { FormActions } from "components/form/actions";
import { FormFieldDescription } from "components/form/description";
import EvtMultiSelectBadges from "components/multiselect";
import SelectType from "components/select/type";
import { PermissionControlledInput } from "permissions/components/input";
import { deliverableSchema } from "./schema";
import { useDeliverableForm } from "./useDeliverableForm";
import { COLLECTION } from "../../constants";

const DeliverableForm = ({ projectId, deliverableId }) => {
  const navigate = useNavigate();
  const { form, requiredFields, handleCreate, handleUpdate, handleRead } =
    useDeliverableForm({ projectId });

  return (
    <Form
      form={form}
      create={handleCreate}
      read={handleRead}
      update={handleUpdate}
      itemId={deliverableId || "+"}
      schema={deliverableSchema}
    >
      {({ isCreating, createItem, isNewItem, isUpdating, updateItem, isDirty }) => {
        const rule = isNewItem ? "create:*" : "update:*";
        return (
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput rule={rule} collection={COLLECTION} type="form">
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Name
                    </FormLabel>
                    <Input {...field} value={field.value ?? ""} />
                  </PermissionControlledInput>
                  <FormFieldDescription collection={COLLECTION} />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput rule={rule} collection={COLLECTION} type="form">
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Type
                    </FormLabel>
                    <SelectType
                      collection={COLLECTION}
                      onValueChange={(val) => field.onChange(val)}
                      {...field}
                    />
                  </PermissionControlledInput>
                  <FormFieldDescription collection={COLLECTION} />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput rule={rule} collection={COLLECTION} type="form">
                    <FormLabel required={requiredFields.includes(field.name)}>
                      URL
                    </FormLabel>
                    <Input type="url" {...field} value={field.value ?? ""} />
                  </PermissionControlledInput>
                  <FormFieldDescription collection={COLLECTION} />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="skills"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput rule={rule} collection={COLLECTION} type="form">
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Skills
                    </FormLabel>
                    <EvtMultiSelectBadges
                      collection="skills"
                      fields={["id", "name"]}
                      searchKeys={["name"]}
                      values={field.value ?? []}
                      onChange={field.onChange}
                      placeholder="Select skills"
                    />
                  </PermissionControlledInput>
                  <FormFieldDescription collection={COLLECTION} />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="files"
              render={({ field }) => (
                <FormItem>
                  <PermissionControlledInput rule={rule} collection={COLLECTION} type="form">
                    <FormLabel required={requiredFields.includes(field.name)}>
                      Files
                    </FormLabel>
                    {/* Files are uploaded on save, so removing one before saving leaves no orphan */}
                    <MultiFileUpload
                      value={field.value ?? []}
                      onChange={field.onChange}
                      accept="image/*,video/*"
                      maxFiles={20}
                    />
                  </PermissionControlledInput>
                  <FormFieldDescription collection={COLLECTION} />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormActions
              isNewItem={isNewItem}
              isCreating={isCreating}
              isUpdating={isUpdating}
              isDirty={isDirty}
              createItem={() => createItem().then(() => navigate(-1))}
              updateItem={() => updateItem().then(() => navigate(-1))}
              showCancel={false}
              collection={COLLECTION}
              rule={rule}
            />
          </div>
        );
      }}
    </Form>
  );
};

export default DeliverableForm;
